import Button from '@platform-ui-common/components/Button/index.js';
import Modal from '@platform-ui-common/components/Modal/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import useLogout from '@platform-ui/auth/hooks/useLogout.js';
import { POSTHOG_EVENTS, POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useIdleTimerContext } from 'react-idle-timer';

interface InactivityDialogProps {
  prompt: boolean;
  setPrompt: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function InactivityDialog({ prompt, setPrompt }: InactivityDialogProps) {
  const { user } = useAuth();
  const { logout, loading } = useLogout();
  const idle = useIdleTimerContext();
  const posthog = usePostHog();
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(idle.getRemainingTime() / 1000));
    }, 500);
    return () => {
      clearInterval(interval);
    };
  });

  const handleStillHere = () => {
    idle.activate();
    posthog.capture(POSTHOG_EVENTS.sessionIdleRecovery);
    setPrompt(false);
  };

  const remainingTimeDisplay = () => {
    const pre = 'Due to inactivity, your session will expire in';
    const post = 'Any unsaved changes will be lost.';
    const minutes =
      Math.floor(remaining / 60) > 0
        ? `${Math.floor(remaining / 60)} minute${remaining >= 120 ? 's' : ''} and`
        : undefined;
    const seconds = `${remaining % 60} seconds.`;
    return [pre, minutes, seconds, post].filter(Boolean).join(' ');
  };

  if (!user?.core && !user?.gcip) return null;
  if (!posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.idleSessionTimeout)) return null;

  return (
    <Modal title="Session Timeout Warning" isOpen={prompt} onClickClose={handleStillHere} size="S">
      <div className="flex flex-col gap-4">
        <Typography variant="body">{remainingTimeDisplay()}</Typography>
        <Typography variant="body">
          For your security, you will be automatically logged out to protect your information. Click
          "Stay Logged In" to continue your session.
        </Typography>
        <div className="flex flex-row gap-4">
          <Button onClick={handleStillHere}>Stay Logged In</Button>
          <Button variant="outline" onClick={logout} loading={loading}>
            Sign Out
          </Button>
        </div>
      </div>
    </Modal>
  );
}
